import { useEffect, useState } from 'react';
import Layout from '../../Layout/Layout';
import { Container, Row, Col, Button, Nav, NavItem, Form, Input, InputGroupText } from 'reactstrap';
import { NavLink } from 'react-router-dom';
import TitleBar from '../../Layout/TitleBar';
import {
  CookbookCategoryTypename,
  DietTypesSliderCookbookCategory,
  ListCookbookCategory,
  SliderCookbookCategory,
  useTranslator,
  withLoading,
} from '@front-activerse/common-components';
import { Link, useNavigate } from 'react-router-dom';
import CookbookMealItem from '../components/CookbookMealItem';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import CookbookRecipeTypeItem from '../components/CookbookRecipeTypeItem';

import { useOutletContext } from 'react-router-dom';
import { useAppContext } from '@front-activerse/common-components';
import CookbookNotificationAlert from '../components/alerts/CookbookNotificationAlert';

interface RemoteConfigFields {
  cookbook_notification_id: number;
  cookbook_notification_img: string;
  cookbook_notification_active: boolean;
  cookbook_notification_content: string;
  cookbook_notification_close_btn_text: string;
  cookbook_notification_redirect_btn_text?: string;
  cookbook_notification_redirect_url_path?: string;
}

interface ICookbookIndexContainer {
  categories: (SliderCookbookCategory | ListCookbookCategory | DietTypesSliderCookbookCategory)[];
  addToFavorites: (mealId: number) => Promise<unknown>;
  removeFromFavorites: (mealId: number) => Promise<unknown>;
  cookbookNavigation: {
    path: string;
    translationKey: string;
  }[];
  categoryAllCoverImageUrl: string;
  categoryAllName: string;
  notificationData: RemoteConfigFields;
  closedNotifications: number[];
  userId: number;
  getClosedNotificationsValues: () => Promise<void>;
  markNotificationAsClosed: (
    collectionName: string,
    userId: number,
    notificationId: number
  ) => Promise<void>;
  loadingMealPlanNotification: boolean;
}

export const CookbookIndexContainer: React.FC<ICookbookIndexContainer> = ({
  categories,
  addToFavorites,
  removeFromFavorites,
  cookbookNavigation,
  categoryAllCoverImageUrl,
  categoryAllName,
  notificationData,
  closedNotifications,
  userId,
  getClosedNotificationsValues,
  markNotificationAsClosed,
  loadingMealPlanNotification,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');

  const {
    images: { cookbookFavMealsIcon, searchIcon },
  } = useAppContext();

  const { trackFirebaseEvent } = useOutletContext<{
    trackFirebaseEvent: (eventName: string, eventPayload: object) => void;
  }>();

  useEffect(() => {
    trackFirebaseEvent('screen_view', {
      firebase_screen: 'Dietplan',
      meals_tab: 'cookbook',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderCategorySlider = (category: SliderCookbookCategory) => {
    if (category.meals.edges.length > 0) {
      return (
        <div className="cookbook-category">
          <h2 className="title-medium semi-bold mb-m">
            {category.name}
            <Button
              color="link"
              size="sm"
              tag={Link}
              to={`/meals/cookbook/category/${category.id}`}
              className="float-end px-0 py-0 text-link-color"
              onClick={() => {
                trackFirebaseEvent('user_action', {
                  action: 'tap-category',
                  location: 'Dietplan',
                  meals_tab: 'cookbook',
                  cookbook_meal_types: category.name,
                });
              }}
            >
              {t('cookbook.main.category.seeAll.button')}
            </Button>
          </h2>
          <div className="cookbook-meals-slider">
            <Row>
              {category.meals.edges.map(({ node }) => (
                <Col xs="6" md="4" key={node.id}>
                  <CookbookMealItem
                    {...node}
                    addToFavorites={addToFavorites}
                    removeFromFavorites={removeFromFavorites}
                    orientation="vertical"
                    tagsVisibility={category.tagsVisibility}
                    displayRating={true}
                  />
                </Col>
              ))}
            </Row>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderCategoryList = (category: ListCookbookCategory) => {
    if (category.meals.edges.length > 0) {
      return (
        <div className="cookbook-category">
          <h2 className="title-medium semi-bold">
            {category.name}
            <Button
              color="link"
              size="sm"
              tag={Link}
              to={`/meals/cookbook/category/${category.id}`}
              state={{ categoryName: category.name }}
              className="float-end px-0 py-0 text-link-color"
              onClick={() => {
                trackFirebaseEvent('user_action', {
                  action: 'tap-category',
                  location: 'Dietplan',
                  meals_tab: 'cookbook',
                  cookbook_meal_types: category.name,
                });
              }}
            >
              {t('cookbook.main.category.seeAll.button')}
            </Button>
          </h2>
          <Row className="cookbook-meals">
            {category.meals.edges.map(({ node }) => (
              <CookbookMealItem
                {...node}
                key={node.id}
                addToFavorites={addToFavorites}
                removeFromFavorites={removeFromFavorites}
                orientation="horizontal"
                tagsVisibility={category.tagsVisibility}
              />
            ))}
          </Row>
        </div>
      );
    }

    return null;
  };

  const renderRecipeTypes = (category: DietTypesSliderCookbookCategory) => {
    if (category.tags.length > 0) {
      return (
        <div className="cookbook-category">
          <h2 className="title-medium semi-bold">{category.name}</h2>
          <div className="cookbook-meals-slider">
            <Row>
              {category.tags.length === 2 && (
                <>
                  <Col xs="8" md="4">
                    <CookbookRecipeTypeItem
                      key={0}
                      id={0}
                      imageUrl={categoryAllCoverImageUrl}
                      name={t(categoryAllName)}
                      mealsCount={1}
                    />
                  </Col>
                  {category.tags.map((tag) => {
                    if (tag.mealsCount > 0) {
                      return (
                        <Col xs="8" md="4" key={tag.id}>
                          <CookbookRecipeTypeItem {...tag} />
                        </Col>
                      );
                    }
                    return null;
                  })}
                </>
              )}
              {category.tags.length === 3 && (
                <>
                  <Col xs="8" md="3">
                    <CookbookRecipeTypeItem
                      key={0}
                      id={0}
                      imageUrl={categoryAllCoverImageUrl}
                      name={t(categoryAllName)}
                      mealsCount={1}
                    />
                  </Col>

                  {category.tags.map((tag) => {
                    if (tag.mealsCount > 0) {
                      return (
                        <Col xs="8" md="3" key={tag.id}>
                          <CookbookRecipeTypeItem {...tag} />
                        </Col>
                      );
                    }
                    return null;
                  })}
                </>
              )}
              {category.tags.length > 3 && (
                <>
                  <Col xs="8" md="3">
                    <CookbookRecipeTypeItem
                      key={0}
                      id={0}
                      imageUrl={categoryAllCoverImageUrl}
                      name={t(categoryAllName)}
                      mealsCount={1}
                    />
                  </Col>

                  {category.tags.map((tag) => {
                    if (tag.mealsCount > 0) {
                      return (
                        <Col xs="8" md="3" key={tag.id}>
                          <CookbookRecipeTypeItem {...tag} />
                        </Col>
                      );
                    }
                    return null;
                  })}
                </>
              )}
            </Row>
          </div>
        </div>
      );
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue) {
      trackFirebaseEvent('user_action', {
        action: 'search',
        location: 'Dietplan',
        meals_tab: 'cookbook',
      });
      navigate(`/meals/cookbook/search?q=${searchValue}`);
      setSearchValue('');
    }
  };

  const renderCookbookNotificationAlert = () => {
    const userClosedNotification = closedNotifications.some(
      (notification) => notification === notificationData.cookbook_notification_id
    );

    if (!userClosedNotification && notificationData.cookbook_notification_active) {
      return (
        <div className="ms-l me-l mt-xl">
          <CookbookNotificationAlert
            userId={userId}
            cookbookNotificationData={notificationData}
            getClosedNotificationsValues={getClosedNotificationsValues}
            markNotificationAsClosed={markNotificationAsClosed}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <TitleBar showBackBtn={false}>
        {t('meals.navbarTitle')}
        <div className="title-bar-icons">
          <Link to="/meals/cookbook/favourite-meals" className="ps-2">
            <img src={cookbookFavMealsIcon} alt="" />
          </Link>
        </div>
      </TitleBar>

      <div className="page-template-cookbook">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                {cookbookNavigation && cookbookNavigation.length > 0 && (
                  <div className="sub-navigation text-center">
                    <Container>
                      <Nav className="d-block">
                        {cookbookNavigation.map((item) => (
                          <NavItem className="w-50">
                            <NavLink to={item.path} replace={true} className="nav-link">
                              {t(item.translationKey)}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </Container>
                  </div>
                )}

                {/* search */}
                <Row>
                  <Col md={{ size: 8, offset: 2 }}>
                    {!loadingMealPlanNotification && renderCookbookNotificationAlert()}
                    <div className="search-container">
                      <h2 className="title-medium semi-bold d-md-none mb-m">
                        {t('meal.search.title')}
                      </h2>
                      <Form onSubmit={handleSubmit}>
                        <div className="input-group-with-addon start">
                          <InputGroupText>
                            <img src={searchIcon} alt="search" />
                          </InputGroupText>
                          <Input
                            id="search"
                            name="search"
                            type="search"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={t('meal.search.inputPlaceholder')}
                          />
                        </div>
                      </Form>
                    </div>
                  </Col>
                </Row>

                {/* categories */}
                {categories.map((category, idx) => {
                  switch (category.__typename) {
                    case CookbookCategoryTypename.SLIDER:
                      return <div key={idx}>{renderCategorySlider(category)}</div>;
                    case CookbookCategoryTypename.LIST:
                      return <div key={idx}>{renderCategoryList(category)}</div>;
                    case CookbookCategoryTypename.DIET_TYPES_SLIDER:
                      return <div key={idx}>{renderRecipeTypes(category)}</div>;
                    default:
                      return '';
                  }
                })}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="" />;

export default withLoading(CookbookIndexContainer, Placeholder);
